import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Camera, X, Loader2, Building2, Pencil, Eye, CheckCircle2, Clock, Undo2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BLUE = '#1C4FC9';
const NAVY = '#0E2A47';

const T = {
  en: {
    title: 'Property Updates', back: 'Back to TEAM',
    lead: 'Report what you see on-site. Your update goes to Operations for verification — it does not change the record until an Operations manager approves it.',
    newUpdate: 'New property update', location: 'Service location', pickLocation: 'Select a location…',
    type: 'Type of update', observation: 'Observation', correction: 'Correct existing info',
    category: 'Area', note: 'What did you observe?', notePh: 'Describe what you saw (condition, issue, change)…',
    onFile: 'What is on file now', onFilePh: 'e.g. "2 floors" / "Gate code 1234"',
    correct: 'What it should be', correctPh: 'e.g. "3 floors" / "Gate code 5588"',
    photos: 'Photos', addPhoto: 'Add photo', submit: 'Submit for verification', submitting: 'Submitting…',
    mySubs: 'My submissions', none: 'You have not submitted any property updates yet.',
    needLoc: 'Choose a location first.', needNote: 'Add a note or a photo.', needCorr: 'Fill in both the current and corrected values.',
    needCat: 'Choose an area first.',
    sent: 'Submitted — Operations will review it.', loadErr: 'Could not load your locations.',
    pending: 'Pending Verification', verified: 'Verified', rejected: 'Not Accepted', returned: 'Returned for Info',
  },
  es: {
    title: 'Actualizaciones de propiedad', back: 'Volver a TEAM',
    lead: 'Reporta lo que ves en el sitio. Tu actualización va a Operaciones para verificación — no cambia el registro hasta que un gerente de Operaciones lo apruebe.',
    newUpdate: 'Nueva actualización', location: 'Ubicación de servicio', pickLocation: 'Selecciona una ubicación…',
    type: 'Tipo de actualización', observation: 'Observación', correction: 'Corregir información existente',
    category: 'Área', note: '¿Qué observaste?', notePh: 'Describe lo que viste (condición, problema, cambio)…',
    onFile: 'Lo que está registrado ahora', onFilePh: 'ej. "2 pisos" / "Código 1234"',
    correct: 'Lo que debería ser', correctPh: 'ej. "3 pisos" / "Código 5588"',
    photos: 'Fotos', addPhoto: 'Agregar foto', submit: 'Enviar para verificación', submitting: 'Enviando…',
    mySubs: 'Mis envíos', none: 'Aún no has enviado actualizaciones de propiedad.',
    needLoc: 'Elige una ubicación primero.', needNote: 'Agrega una nota o una foto.', needCorr: 'Completa el valor actual y el corregido.',
    needCat: 'Elige un área primero.',
    sent: 'Enviado — Operaciones lo revisará.', loadErr: 'No se pudieron cargar tus ubicaciones.',
    pending: 'Pendiente de verificación', verified: 'Verificado', rejected: 'No aceptado', returned: 'Devuelto por info',
  },
};

const STATUS = {
  pending_verification: { key: 'pending', icon: Clock, cls: 'bg-amber-50 text-amber-700' },
  verified: { key: 'verified', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { key: 'rejected', icon: XCircle, cls: 'bg-rose-50 text-rose-700' },
  returned: { key: 'returned', icon: Undo2, cls: 'bg-sky-50 text-sky-700' },
};

const StatusPill = ({ status, t }) => {
  const s = STATUS[status] || STATUS.pending_verification;
  const Icon = s.icon;
  return (
    <span data-testid={`prop-status-${s.key}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${s.cls}`}>
      <Icon size={13} /> {t[s.key]}
    </span>
  );
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: '2-digit', hour: 'numeric', minute: '2-digit' }).format(d);
};

const TeamPropertyUpdates = ({ lang = 'en', token, onBack }) => {
  const t = T[lang] || T.en;
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [locs, setLocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('observation');
  const [form, setForm] = useState({ location_id: '', category: '', note: '', target_field: '', current_value: '', proposed_value: '' });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const label = (c) => (lang === 'es' ? c.es : c.en);

  const loadMine = () => axios.get(`${API}/api/location-contributions/mine`, H).then((r) => setMine(r.data)).catch(() => {});

  useEffect(() => {
    let on = true;
    Promise.all([
      axios.get(`${API}/api/location-contributions/my-locations`, H),
      axios.get(`${API}/api/location-contributions/categories`, H),
      axios.get(`${API}/api/location-contributions/mine`, H),
    ]).then(([l, c, m]) => {
      if (!on) return;
      setLocs(l.data); setCats(c.data); setMine(m.data);
      if (l.data.length === 1) setForm((f) => ({ ...f, location_id: l.data[0].id }));
    }).catch(() => on && toast.error(t.loadErr)).finally(() => on && setLoading(false));
    return () => { on = false; };
    // eslint-disable-next-line
  }, [token]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files.slice(0, 6)) {
        const data = await fileToDataUrl(f);
        const r = await axios.post(`${API}/api/location-contributions/photo`, { data }, H);
        setPhotos((p) => [...p, r.data]);
      }
    } catch { toast.error('Photo upload failed'); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const reset = () => { setForm({ location_id: form.location_id, category: '', note: '', target_field: '', current_value: '', proposed_value: '' }); setPhotos([]); setKind('observation'); };

  const submit = async () => {
    if (!form.location_id) return toast.error(t.needLoc);
    if (!form.category) return toast.error(t.needCat);
    if (kind === 'correction' && (!form.target_field.trim() || !form.proposed_value.trim())) return toast.error(t.needCorr);
    if (kind === 'observation' && !form.note.trim() && !photos.length) return toast.error(t.needNote);
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/location-contributions`, { ...form, kind, photos }, H);
      toast.success(t.sent);
      reset();
      await loadMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="team-property-updates">
      <button onClick={onBack} data-testid="prop-back" className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1C4FC9]"><ArrowLeft size={16} /> {t.back}</button>

      <div className="mb-5 flex items-start gap-3">
        <span className="rounded-xl bg-white p-2 shadow-sm ring-1 ring-slate-200"><Building2 size={26} style={{ color: BLUE }} /></span>
        <div>
          <h1 className="text-2xl font-extrabold" style={{ color: NAVY }}>{t.title}</h1>
          <p className="mt-1 max-w-xl text-sm text-slate-600">{t.lead}</p>
        </div>
      </div>

      {/* New update */}
      <div className="rounded-3xl border border-slate-200 bg-[#FBFAF6] p-5 shadow-sm" data-testid="prop-form">
        <h2 className="mb-3 text-base font-bold" style={{ color: NAVY }}>{t.newUpdate}</h2>

        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.location}</label>
        <select data-testid="prop-location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}
          className="mb-4 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
          <option value="">{t.pickLocation}</option>
          {locs.map((l) => <option key={l.id} value={l.id}>{l.location_name}{l.customer_name ? ` — ${l.customer_name}` : ''}</option>)}
        </select>

        <div className="mb-4 grid grid-cols-2 gap-2">
          {[['observation', Eye, t.observation], ['correction', Pencil, t.correction]].map(([k, Icon, lbl]) => (
            <button key={k} type="button" data-testid={`prop-kind-${k}`} onClick={() => setKind(k)}
              className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${kind === k ? 'border-[#1C4FC9] bg-[#1C4FC9] text-white' : 'border-slate-300 bg-white text-slate-700'}`}>
              <Icon size={15} /> {lbl}
            </button>
          ))}
        </div>

        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.category}</label>
        <select data-testid="prop-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="mb-4 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
          <option value="">—</option>
          {cats.map((c) => <option key={c.key} value={c.key}>{label(c)}</option>)}
        </select>

        {kind === 'observation' ? (
          <>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.note}</label>
            <textarea data-testid="prop-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={4}
              placeholder={t.notePh} className="mb-4 w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" />
          </>
        ) : (
          <div className="mb-4 grid gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.onFile}</label>
              <input data-testid="prop-target" value={form.target_field} onChange={(e) => setForm({ ...form, target_field: e.target.value })}
                placeholder={t.onFilePh} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.correct}</label>
              <input data-testid="prop-proposed" value={form.proposed_value} onChange={(e) => setForm({ ...form, proposed_value: e.target.value })}
                placeholder={t.correctPh} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" />
            </div>
            <textarea data-testid="prop-corr-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
              placeholder={t.notePh} className="w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm" />
          </div>
        )}

        {/* Photos */}
        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t.photos}</label>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {photos.map((p, i) => (
            <div key={p.id || i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200">
              <img src={`${API}${p.url}&auth=${token}`} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setPhotos(photos.filter((_, k) => k !== i))}
                className="absolute right-0 top-0 rounded-bl bg-black/60 p-0.5 text-white"><X size={12} /></button>
            </div>
          ))}
          <button type="button" data-testid="prop-add-photo" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#1C4FC9] hover:text-[#1C4FC9]">
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <><Camera size={18} /><span className="text-[9px] font-semibold">{t.addPhoto}</span></>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPick} />
        </div>

        <button data-testid="prop-submit" onClick={submit} disabled={submitting}
          className="w-full rounded-xl bg-[#0E2A47] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#15375e] disabled:opacity-60">
          {submitting ? <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> {t.submitting}</span> : t.submit}
        </button>
      </div>

      {/* My submissions */}
      <h2 className="mb-3 mt-7 text-base font-bold" style={{ color: NAVY }}>{t.mySubs}</h2>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-slate-400"><Loader2 className="animate-spin" size={18} /> …</div>
      ) : mine.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-center text-sm text-slate-400" data-testid="prop-empty">{t.none}</p>
      ) : (
        <div className="space-y-2.5" data-testid="prop-my-list">
          {mine.map((c) => (
            <div key={c.id} data-testid={`prop-item-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-bold" style={{ color: NAVY }}>{c.category_label}</span>
                <StatusPill status={c.status} t={t} />
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-500">{c.location_name} · {fmtDate(c.submitted_at)}</div>
              {c.kind === 'correction' ? (
                <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs">
                  <div className="text-slate-500">{c.target_field}</div>
                  <div className="font-semibold text-[#0E2A47]">→ {c.proposed_value}</div>
                  {c.note && <div className="mt-1 text-slate-600">{c.note}</div>}
                </div>
              ) : c.note && <p className="mt-2 text-sm text-slate-700">{c.note}</p>}
              {c.photos?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.photos.map((p, i) => <img key={i} src={`${API}${p.url}&auth=${token}`} alt="" className="h-12 w-12 rounded border border-slate-200 object-cover" />)}
                </div>
              )}
              {c.verification_note && (c.status === 'rejected' || c.status === 'returned') && (
                <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600"><span className="font-semibold">Operations:</span> {c.verification_note}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamPropertyUpdates;
